import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';

import type FlightList from '@this/domain/flight/flight_list';
import type { FlightType } from '@this/domain/search_query';
import { FLIGHT_TYPES } from '@this/domain/search_query';
import type SelectStore from '@this/src/domain/select_store';
import type SelectRepository from '@this/src/domain/select_repository';
import type SearchQueryItem from '@this/src/domain/search_query_item';
import type SearchResultItem from '@this/src/domain/search_result_item';

import FlightListComponent from '../flight_list';
import PopupListSelector from '../../popup_list_selector/PopupListSelector';
import PopupListMultiSelector from '../../popup_list_multi_selector/popup_list_multi_selector';

interface FlightListAreaProps {
  store: SelectStore;
  repository: SelectRepository;
  icon: string;
  listWrapper: FlightList;
  subtype: number;
  queryItem?: SearchQueryItem;
  resultItem?: SearchResultItem;
  shownFlightFilter: boolean;
  setShownFlightFilter: (shown: boolean) => void;
}

const FlightListArea: React.FC<FlightListAreaProps> = ({
  store,
  repository,
  icon,
  listWrapper,
  subtype,
  queryItem,
  resultItem,
  shownFlightFilter,
  setShownFlightFilter
}) => {
  const { theme } = useAITTheme();

  const styles = useMemo(() => getStyles(theme), [theme]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          marginBottom: '10px'
        }}
      >
        <span>
          <div style={styles.icon}>{icon}</div>
          <div style={styles.title}>
            <span>すべての候補(</span>
            <span data-wovn-ignore>{listWrapper.length({ subtype: subtype || 0 })}</span>
            <span>件)</span>
          </div>
        </span>
        <div style={styles.foreignFlightConditions}>
          <button
            type="button"
            style={styles.toggleButton}
            className="flight-list-area__toggle-button"
            onClick={() => setShownFlightFilter(shownFlightFilter === false)}
          >
            {shownFlightFilter ? '詳細絞り込みを非表示' : '詳細絞り込みを表示'}
          </button>
        </div>
      </div>
      {resultItem?.rakutenStaticFileUpdatedAt && (
        <p style={styles.staticFileUpdatedAtText}>
          在庫データ：{resultItem.rakutenStaticFileUpdatedAt.format('YYYY年MM月DD日 HH時mm分')}更新
        </p>
      )}
      {shownFlightFilter && (
        <>
          <div className="select-pc__popup-list-wrapper">
            <div style={styles.popupSubWrapper}>
              <span style={styles.popupListMiniTitle}>+航空会社</span>
              <PopupListMultiSelector
                items={(store.result.query.carrierList || store.result.flightList?.carrierList || []).map(
                  carrier => {
                    return { label: carrier.name, value: carrier.id };
                  }
                )}
                headerLabel="航空会社"
                selectableAll
                selectedValues={queryItem?.carrierIds || []}
                onChange={(values: string[]) => {
                  queryItem?.setCarrierIds(values);
                  listWrapper.handleAirlinesChange(subtype, values);
                }}
                onSelectAll={() => {
                  queryItem?.setCarrierIds(['all']);
                  listWrapper.resetAirlines(subtype);
                }}
              />
            </div>
            {queryItem?.airports && (
              <>
                <div style={styles.popupSubWrapper}>
                  <span style={styles.popupListMiniTitle}>+出発空港</span>
                  <PopupListSelector
                    items={queryItem.airports.origin}
                    headerLabel="出発空港"
                    name="originCode"
                    currentValue={queryItem.originCode || queryItem.airports.origin[0].value}
                    onChange={(value: string) => {
                      queryItem.setOriginCode(value);
                      if (queryItem.airports && queryItem.airports.origin[0].value === value) {
                        listWrapper.resetOriginCode(subtype);
                      } else {
                        listWrapper.handleOriginCodeChange(subtype, value);
                      }
                    }}
                  />
                </div>
                <div style={styles.popupSubWrapper}>
                  <span style={styles.popupListMiniTitle}>+到着空港</span>
                  <PopupListSelector
                    items={queryItem.airports.destination}
                    headerLabel="到着空港"
                    name="destCode"
                    currentValue={queryItem.destCode || queryItem.airports.destination[0].value}
                    onChange={(value: string) => {
                      queryItem.setDestCode(value);
                      if (queryItem.airports && queryItem.airports.destination[0].value === value) {
                        listWrapper.resetDestCode(subtype);
                      } else {
                        listWrapper.handleDestCodeChange(subtype, value);
                      }
                    }}
                  />
                </div>
              </>
            )}
            <div style={styles.popupSubWrapper}>
              <span style={styles.popupListMiniTitle}>+シートクラス</span>
              <PopupListMultiSelector
                items={[
                  { label: 'エコノミークラス', value: 'M' },
                  { label: 'プレミアムエコノミー', value: 'W' },
                  { label: 'ビジネスクラス', value: 'C' },
                  { label: 'ファーストクラス', value: 'F' }
                ]}
                headerLabel="シートクラス"
                selectableAll
                selectedValues={store.result.query.cabin}
                defaultSeatClasses={store.result.query.defaultSeatClassesForFilter()}
                onChange={(values: string[]) => {
                  repository.updateCabin(values);
                  listWrapper.handleCabinsChange(values);
                }}
                onSelectAll={() => {
                  repository.updateCabin(['all']);
                }}
              />
            </div>
          </div>
          <div className="select-pc__popup-list-wrapper">
            <div style={styles.popupSubWrapper}>
              <span style={styles.popupListMiniTitle}>+乗り継ぎ条件</span>
              <PopupListSelector
                items={[
                  { label: 'すべて', value: '' },
                  { label: '乗り継ぎのみ', value: 'C' },
                  { label: '直行便のみ', value: 'N' }
                ]}
                headerLabel="乗り継ぎ条件"
                name="flightType"
                currentValue={store.result.query.flightType || ''}
                onChange={(key: string) => {
                  if (!FLIGHT_TYPES.some(f => f === key)) {
                    return;
                  }

                  const flightType = key as FlightType;
                  repository.updateFlightType(flightType);
                  listWrapper.handleFlightType(subtype, flightType);
                }}
              />
            </div>
            <div style={styles.popupSubWrapper}>
              <span style={styles.popupListMiniTitle}>+変更・キャンセル条件</span>
              <PopupListSelector
                items={[
                  { label: 'すべて', value: 'all' },
                  { label: '変更可のみ', value: 'changeable' },
                  { label: 'キャンセル可のみ', value: 'cancelable' },
                  { label: '変更＆キャンセル可のみ', value: 'both' }
                ]}
                headerLabel="変更・キャンセル条件"
                name="extraKey"
                currentValue={listWrapper.minirule}
                onChange={(key: string) => {
                  if (key === 'all' || key === 'changeable' || key === 'cancelable' || key === 'both') {
                    listWrapper.handleMiniruleChange(key);
                  }
                }}
              />
            </div>
          </div>
        </>
      )}
      <div style={styles.sortTabs}>
        <div
          style={styles.sortTab(listWrapper.sortKey === 'orderedIndex')}
          className="flight-list-area__sort-tab"
          role="button"
          tabIndex={0}
          onClick={listWrapper.handleSortKeyChange('orderedIndex')}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              listWrapper.handleSortKeyChange('orderedIndex')();
            }
          }}
        >
          おすすめ順
        </div>
        <div
          style={styles.sortTab(listWrapper.sortKey === 'price')}
          className="flight-list-area__sort-tab"
          role="button"
          tabIndex={0}
          onClick={listWrapper.handleSortKeyChange('price')}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              listWrapper.handleSortKeyChange('price')();
            }
          }}
        >
          料金が安い順
        </div>
        <div
          style={styles.sortTab(listWrapper.sortKey === 'departure')}
          className="flight-list-area__sort-tab"
          role="button"
          tabIndex={0}
          onClick={listWrapper.handleSortKeyChange('departure')}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              listWrapper.handleSortKeyChange('departure')();
            }
          }}
        >
          出発時刻が早い順
        </div>
        <div
          style={styles.sortTab(listWrapper.sortKey === 'time')}
          className="flight-list-area__sort-tab"
          role="button"
          tabIndex={0}
          onClick={listWrapper.handleSortKeyChange('time')}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              listWrapper.handleSortKeyChange('time')();
            }
          }}
        >
          所要時間が短い順
        </div>
      </div>
      <div style={styles.body}>
        <FlightListComponent
          flights={listWrapper}
          type={subtype}
          repository={repository}
          store={store}
          onChange={store.setTab('none')}
        />
      </div>
    </>
  );
};

const getStyles = (theme: AITTheme) => ({
  icon: {
    display: 'inline-block',
    textAlign: 'center' as const,
    width: 20,
    height: 20,
    background: theme.iconColor,
    borderRadius: 10,
    color: 'white',
    fontWeight: 'bold' as const,
    marginRight: 10
  },
  title: {
    display: 'inline-block',
    fontWeight: 'bold' as const,
    marginRight: 10
  },
  foreignFlightConditions: {
    fontSize: 12
  },
  sortTabs: {
    display: 'flex',
    justifyContent: 'space-between' as const
  },
  sortTab: (active: boolean) => ({
    width: 'calc((100% - 5px * 3) / 4)',
    marginBottom: 15,
    paddingBottom: 3,
    fontSize: 10,
    fontWeight: 'bold' as const,
    borderBottom: `2px solid ${active ? theme.linkColor : theme.grayTextColor}`,
    color: active ? theme.linkColor : theme.grayTextColor
  }),
  staticFileUpdatedAtText: {
    width: '100%',
    textAlign: 'right' as const,
    fontSize: 10,
    color: theme.grayTextColor,
    marginBottom: 15
  },
  body: {
    display: 'flex',
    flexDirection: 'column' as const,
    maxHeight: '66vh',
    overflowY: 'scroll' as const,
    WebkitOverflowScrolling: 'touch' as const
  },
  popupListMiniTitle: {
    fontSize: 10,
    color: '#404040',
    marginBottom: 3
  },
  popupSubWrapper: {
    marginBottom: 10
  },
  toggleButton: {
    color: theme.linkColor
  }
});

export default FlightListArea;
